import Box from "@mui/material/Box";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { DropdownButton } from "../../../components/ui/Dropdown";
import { Icon } from "../../../components/ui/Icon";
import { Surface } from "../../../components/ui/Surface";
import { Command, MenuListProps } from "../types";

export const MenuList = React.forwardRef((props: MenuListProps, ref) => {
    const scrollContainer = useRef<HTMLDivElement>(null);
    const activeItem = useRef<HTMLButtonElement>(null);
    const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);
    const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);

    // Anytime the groups change, i.e. the user types to narrow it down, we want to
    // reset the current selection to the first menu item
    useEffect(() => {
        setSelectedGroupIndex(0);
        setSelectedCommandIndex(0);
    }, [props.items]);

    const selectItem = useCallback(
        (groupIndex: number, commandIndex: number) => {
            const command = props.items[groupIndex].commands[commandIndex];
            props.command(command);
        },
        [props]
    );

    React.useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
            if (event.key === "ArrowDown") {
                if (!props.items.length) {
                    return false;
                }

                const commands = props.items[selectedGroupIndex].commands;

                let newCommandIndex = selectedCommandIndex + 1;
                let newGroupIndex = selectedGroupIndex;

                if (commands.length - 1 < newCommandIndex) {
                    newCommandIndex = 0;
                    newGroupIndex = selectedGroupIndex + 1;
                }

                if (props.items.length - 1 < newGroupIndex) {
                    newGroupIndex = 0;
                }

                setSelectedCommandIndex(newCommandIndex);
                setSelectedGroupIndex(newGroupIndex);

                return true;
            }

            if (event.key === "ArrowUp") {
                if (!props.items.length) {
                    return false;
                }

                let newCommandIndex = selectedCommandIndex - 1;
                let newGroupIndex = selectedGroupIndex;

                if (newCommandIndex < 0) {
                    newGroupIndex = selectedGroupIndex - 1;
                    newCommandIndex = props.items[newGroupIndex]?.commands.length - 1 || 0;
                }

                if (newGroupIndex < 0) {
                    newGroupIndex = props.items.length - 1;
                    newCommandIndex = props.items[newGroupIndex].commands.length - 1;
                }

                setSelectedCommandIndex(newCommandIndex);
                setSelectedGroupIndex(newGroupIndex);

                return true;
            }

            if (event.key === "Enter") {
                if (!props.items.length || selectedGroupIndex === -1 || selectedCommandIndex === -1) {
                    return false;
                }

                selectItem(selectedGroupIndex, selectedCommandIndex);

                return true;
            }

            return false;
        },
    }));

    useEffect(() => {
        if (activeItem.current && scrollContainer.current) {
            const offsetTop = activeItem.current.offsetTop;
            const offsetHeight = activeItem.current.offsetHeight;

            scrollContainer.current.scrollTop = offsetTop - offsetHeight;
        }
    }, [selectedCommandIndex, selectedGroupIndex]);

    const createCommandClickHandler = useCallback(
        (groupIndex: number, commandIndex: number) => {
            return () => {
                selectItem(groupIndex, commandIndex);
            };
        },
        [selectItem]
    );

    if (!props.items.length) {
        return null;
    }

    return (
        <Surface
            ref={scrollContainer}
            style={{
                color: "#000000",
                maxHeight: "min(80vh, 384px)",
                overflowY: "auto",
                flexWrap: "wrap",
                marginBottom: "32px",
                padding: "8px",
            }}>
            <div
                style={{ display: "grid", gridTemplateColumns: "1fr", gap: "2px" }}
                data-testid="auto-id-deae7bdf-5d9c-4e83-920a-86b3ae72cada">
                {props.items.map((group, groupIndex: number) => (
                    <React.Fragment key={`${group.title}-wrapper`}>
                        <Box
                            sx={{
                                color: "#737373",
                                fontSize: "10.4px",
                                gridColumn: "1 / -1",
                                marginLeft: "8px",
                                marginRight: "8px",
                                marginTop: "16px",
                                fontWeight: 600,
                                letterSpacing: "0.05em",
                                userSelect: "none",
                                textTransform: "uppercase",
                                "&:first-of-type": {
                                    marginTop: "2px",
                                },
                            }}
                            key={`${group.title}`}>
                            {group.title}
                        </Box>
                        {group.commands.map((command: Command, commandIndex: number) => (
                            <DropdownButton
                                key={`${command.label}`}
                                isActive={selectedGroupIndex === groupIndex && selectedCommandIndex === commandIndex}
                                onClick={createCommandClickHandler(groupIndex, commandIndex)}>
                                <Icon
                                    name={command.iconName}
                                    style={{ marginRight: "4px" }}
                                    iconProps={command?.iconProps}
                                />
                                {command.label}
                            </DropdownButton>
                        ))}
                    </React.Fragment>
                ))}
            </div>
        </Surface>
    );
});

export default MenuList;
